import angular from 'angular'
import { endOfDay, format, isAfter, isBefore, isValid } from 'date-fns'

import { dateFormat } from '../../constants/vd-date-time'

/**
 * Directive to attach to an input element to validate and format the value as a date.
 *
 * Usage:
 *
 * <input vd-date class="vd-input">
 * <input vd-date="{ minDate: '2017-01-01', format: 'd MMM yy' }" class="vd-input">
 *
 * Optionally specify some config to determine how to validate the date.
 *
 *    [format] {String} The date-fns date format to use; defaults to d MMM yyyy
 *    [minDate] {Date|String} The minimum date the value is allowed to be. Strings must be ISO 8601-formatted.
 *    [maxDate] {Date|String} The maximum date the value is allowed to be. Strings must be ISO 8601-formatted.
 */
export default function DateDirective($timeout) {
  'ngInject'

  return {
    require: 'ngModel',
    restrict: 'A',
    link($scope, $element, $attrs, ngModelCtrl) {
      ngModelCtrl.$validators.date = function (value) {
        return !value || isValidDate(value)
      }
      ngModelCtrl.$validators.dateRange = isValidWithinRange

      ngModelCtrl.$formatters.push(formatDate)
      ngModelCtrl.$parsers.push(formatDate)

      $element.on('blur', () => $timeout(setViewValue))

      let dateConfig = {}
      $scope.$watch(
        () => $scope.$eval($attrs.vdDate),
        vdDate => {
          dateConfig = vdDate || {}
          ngModelCtrl.$validate()

          if (angular.isDefined(ngModelCtrl.$modelValue)) {
            // View value to be set with new rules
            setViewValue()
          }
        },
        true
      )

      /**
       * Get a Date of the value.
       *
       * @private
       * @method getDate
       *
       * @param  value {String}
       *
       * @return {Date} Date of the value, or the value itself if it is not truthy (e.g. undefined)
       */
      function getDate(value) {
        return value && new Date(value)
      }

      /**
       * Determine if the given value is a valid date.
       *
       * @private
       * @method isValidDate
       *
       * @param  value {String}
       *
       * @return {Boolean} true if the value is a valid date
       */
      function isValidDate(value) {
        const date = getDate(value)
        return isValid(date)
      }

      /**
       * Determine if the given value is a valid date within the configured `minDate` and `maxDate` dates.
       *
       * @private
       * @method isValidWithinRange
       *
       * @param  value {String}
       *
       * @return {Boolean} true if the value is a valid date within the configured range
       */
      function isValidWithinRange(value) {
        if (!value || (!dateConfig.minDate && !dateConfig.maxDate)) {
          return true
        }

        if (!isValidDate(value)) {
          return false
        }

        const { minDate, maxDate } = dateConfig
        const date = getDate(value)

        if (minDate && isBefore(endOfDay(date), new Date(minDate))) {
          return false
        }

        if (maxDate && isAfter(date, endOfDay(new Date(maxDate)))) {
          return false
        }

        return true
      }

      /**
       * Format the given value for display.
       *
       * @private
       * @method formatDate
       *
       * @param  value {String|undefined}
       *
       * @return {String} the value formatted as a date according to the configured date format
       */
      function formatDate(value) {
        const date = new Date(value)
        if (!value || !isValidDate(date)) {
          return value
        }

        const formatString = dateConfig.format || dateFormat
        return format(date, formatString)
      }

      /**
       * Update the view value assuming the input is not currently invalid.
       *
       * @private
       * @method setViewValue
       */
      function setViewValue() {
        // Leave invalid values intact so that user can see the error and fix it.
        if (ngModelCtrl.$invalid) {
          return
        }

        $scope.$applyAsync(() => {
          const formatted = formatDate(ngModelCtrl.$modelValue)
          ngModelCtrl.$setViewValue(formatted)
          ngModelCtrl.$render()
        })
      }
    },
  }
}
